"use client";
import React, { useState, useEffect, useRef } from "react";
import { Edit, Trash2, Eye, Plus, X, UploadCloud, ChevronsLeft, ChevronsRight } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";

import { useEmployees } from "../../context/EmployeeContext";
import { useAuth } from "../../context/AuthContext";
import { useDepartments } from "../../context/DepartmentContext";
import { useDesignation } from "../../context/DesignationContext";
import { useRoles } from "../../context/RoleContext";

export default function Employee() {
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [duration, setDuration] = useState("6 months");
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");

  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const modalRef = useRef(null);
const [isMobile, setIsMobile] = useState(false);

// Returns yesterday's date in YYYY-MM-DD format so the DOB field
// can never be set to today or a future date.
const getMaxDob = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split("T")[0];
};

useEffect(() => {
  const handleResize = () => {
    setIsMobile(window.innerWidth < 640);
  };

  handleResize(); // initial check
  window.addEventListener("resize", handleResize);

  return () => window.removeEventListener("resize", handleResize);
}, []);
  const { employees: ctxEmployees, 
    loading, 
    error, 
    fetchEmployees, 
    departmentChart, 
    createEmployee,
    employeeStatuses,
    employeeTypes,
    workLocations,
    statusLoading,
    statusError,
    typeLoading,
    typeError,
    workLocationLoading,
    workLocationError,
    updateEmployee,
    deleteEmployee,
  } = useEmployees();

  const { isAuthenticated, token, canDo } = useAuth();

  const { departments, loading: deptLoading, error: deptError } = useDepartments();

  const { designations, loading: desigLoading, error: desigError } = useDesignation();

  const { roles, loading: roleLoading, error: roleError } = useRoles();
  
  const [preview, setPreview] = useState(null);

  const [activeIndex, setActiveIndex] = useState(null);

  // Close every modal, used before opening a different one so that
  // Create / View / Edit / Delete can never be visible at the same time.
  const closeAllModals = () => {
    setShowModal(false);
    setShowViewModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
  };

const handleImageChange = (e) => {
  const file = e.target.files[0];
  if (file) {
    // Optional: validate file type and size
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      alert("Only JPG, JPEG, or PNG files are allowed.");
      e.target.value = "";
      return;
    }
    if (file.size > 1024 * 1024) {
      alert("File size must be less than 1MB.");
      e.target.value = "";
      return;
    }

    // Preview image
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);
  }
};


  // Pie Chart Data
  const data = departmentChart;
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    setEmployees(Array.isArray(ctxEmployees) ? ctxEmployees : []);
  }, [ctxEmployees]);
  
  const statusColors = {
    Active: "bg-green-100 text-green-700",
    Inactive: "bg-red-100 text-red-700",
    Onboarding: "bg-yellow-100 text-yellow-700",
    Ongoing: "bg-orange-100 text-orange-700",
  };

  /// 🔍 Filter + Search Logic
 const filteredEmployees = employees.filter((e) => {
  const fullName = `${e.first_name} ${e.last_name}`.toLowerCase();

  const matchesSearch =
    e.id.toLowerCase().includes(searchTerm.toLowerCase()) || 
    fullName.includes(searchTerm.toLowerCase()) ||
    e.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.department?.toLowerCase().includes(searchTerm.toLowerCase());

  const matchesFilter = filterStatus === "All" || e.status === filterStatus;
  return matchesSearch && matchesFilter;
});

  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;
  const paginatedEmployees = filteredEmployees.slice(startIndex, startIndex + itemsPerPage);

  const [formData, setFormData] = useState({
    role_id: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    department: "",
    designation: "",
    reporting_manager: "",
    gender: "Male",
    work_location: "",
    employee_type: "",
    status: "",
    address: "",
    postcode: "",
    joiningdate: "",
    bank: "",
    ifsc: "",
    acc_no: "",
    pan_no: "",
    uan_no: "",
    salary: "",
    dob: "",
    image: null
  });

  // Close the Create modal and reset any transient state (error/success/preview)
  const closeCreateModal = () => {
    setShowModal(false);
    setPreview(null);
    setErrorMessage("");
  };

  // Handle Add Employee Form Submission
  const handleSubmit = async (e) => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const userId = user?.id || null;

    e.preventDefault();

    const form = e.target;
    setErrorMessage(""); // Clear previous error
    setSuccessMessage(""); // Clear previous success

    // Get file safely
    const fileInput = form.querySelector("#fileInput");
    const file = fileInput?.files?.[0] || null;

    // Validate file
    if (!file) {
      setErrorMessage("Please select an image.");
      if (modalRef.current) {
        modalRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
      return; // stop submission
    }

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      setErrorMessage("Only JPG, JPEG, or PNG files are allowed.");
      if (modalRef.current) modalRef.current.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    if (file.size > 1024 * 1024) {
      setErrorMessage("Image size must be less than 1MB.");
      if (modalRef.current) modalRef.current.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    // Create FormData
    const formData = new FormData();
    formData.append("role_id", form.role_id.value);
    formData.append("first_name", form.firstName.value);
    formData.append("last_name", form.lastName.value);
    formData.append("email", form.email.value);
    formData.append("phone", form.phone.value);
    formData.append("department_id", form.department.value);
    formData.append("designation_id", form.designation.value);
    formData.append("reporting_manager", form.reporting_manager.value || "");
    formData.append("gender", form.gender.value);
    formData.append("workLocation_id", form.work_location.value);
    formData.append("empType", form.employee_type.value);
    formData.append("status", form.status.value);
    formData.append("address", form.address.value);
    formData.append("postcode", form.postcode.value);
    formData.append("joiningdate", form.joiningdate.value);
    formData.append("bank", form.bank.value);
    formData.append("ifsc", form.ifsc.value);
    formData.append("acc_no", form.acc_no.value);
    formData.append("pan_no", form.pan_no.value);
    formData.append("uan_no", form.uan_no.value);
    formData.append("salary", form.salary.value);
    formData.append("dob", form.dob.value);
    formData.append("create_audit_id", userId);

    if (file) {
        formData.append("image", file);
    }

    // Debug: log FormData keys/values
    for (let [key, value] of formData.entries()) {
        console.log(`${key}:`, value);
    }

    try {
        const result = await createEmployee(formData);

        if (result.success) {
            setSuccessMessage(result.message || "Employee created successfully!");
            form.reset(); // Only reset on success
            setPreview(null); // Clear the picture preview since form.reset() doesn't touch React state
            setShowModal(false); // Close modal
        } else {
            // ❌ On error: show message, keep modal & form data intact
            setErrorMessage(result.message || "Failed to create employee");

  // Scroll modal to top so error is visible
  if (modalRef.current) {
    modalRef.current.scrollTo({ top: 0, behavior: "smooth" });
  }
        }
    } catch (err) {
        // ❌ Network or unexpected errors
        console.error("Error creating employee:", err);
        setErrorMessage("Network error. Please try again."); // Modal stays open
    }
};

 const handleEditSubmit = async (e) => {
  e.preventDefault();

  setErrorMessage("");
  setSuccessMessage("");

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const createAuditId = user?.id || null;

  const form = e.target;
  const id = selectedEmployee.empId;

  // Get file safely
  const fileInput = form.querySelector("#fileInput");
  const file = fileInput?.files?.[0] || null;

  // Optional: Validate file type & size
  if (file) {
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      setErrorMessage("Only JPG, JPEG, or PNG files are allowed.");
      return;
    }
    if (file.size > 1024 * 1024) {
      setErrorMessage("Image size must be less than 1MB.");
      return;
    }
  }

  // Create FormData
  const formData = new FormData();
  formData.append("role_id", form.role_id.value);
  formData.append("first_name", form.first_name.value);
  formData.append("last_name", form.last_name.value);
  formData.append("email", form.email.value);
  formData.append("phone", form.phone.value);
  formData.append("department_id", form.department.value);
  formData.append("designation_id", form.designation.value);
  formData.append("reporting_manager", form.reporting_manager.value || "");
  formData.append("gender", form.gender?.value || selectedEmployee.gender || "Male");
  formData.append("workLocation_id", form.work_location.value);
  formData.append("empType", form.employee_type.value);
  formData.append("status", form.status.value);
  formData.append("exit_date", form.exit_date.value);
  formData.append("address", form.address.value);
  formData.append("postcode", form.postcode.value);
  formData.append(
    "joiningdate",
    form.joiningDate.value || selectedEmployee.joiningDate?.split("T")[0] || ""
  );
  formData.append("bank", form.bank.value);
  formData.append("ifsc", form.ifsc.value);
  formData.append("acc_no", form.acc_no.value);
  formData.append("pan_no", form.pan_no.value);
  formData.append("uan_no", form.uan_no.value);
  // formData.append("pf_no", form.pf_no.value);
  formData.append("salary", form.salary.value);
  formData.append("dob", form.dob.value);
  formData.append("update_audit_id", createAuditId);

  if (file) {
    formData.append("image", file);
  }

  // Call context function to update employee
  const result = await updateEmployee(id, formData);

  if (result.success) {
    setSuccessMessage(result.message || "Employee updated successfully!");
    setPreview(null);
    setShowEditModal(false);
  } else {
    setErrorMessage(result.message || "Failed to update employee.");
  }
};



  const handleDeleteEmployee = async () => {
    if (!selectedEmployee) return;

      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const deleteAuditId = user?.id || null;

      const result = await deleteEmployee(selectedEmployee.empId, deleteAuditId);
      alert(result.message);
      setShowDeleteModal(false);
  };

  return (
    <>
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap"
      />

      <div className=" bg-sky-50">

         <div className="px-6 py-6 sm:px-9 max-sm:px-4 ">
           <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-5">
  Employee Overview
</h1>
            {/* Tech Team Composition */}
            <div className="bg-white rounded-2xl shadow-sm p-8 mb-8">
              <div className="mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h2 className="text-2xl font-semibold text-gray-900">
                      Team Composition
                    </h2>
                    <div className="flex mt-2 rounded-xl overflow-hidden w-70 h-3">
                      <div className="flex-1 bg-[#9e4bff]"></div>
                      <div className="flex-1 bg-[#ffa500]"></div>
                      <div className="flex-1 bg-[#003366]"></div>
                      <div className="flex-1 bg-[#ffd700]"></div>
                      <div className="flex-1 bg-[#14b8a6]"></div>
                      <div className="flex-1 bg-[#2249d2]"></div>
                    </div>
                  </div>
              

                </div>
              </div>

              <div className="flex flex-col lg:flex-row gap-10 items-center">
                {/* Left Side - Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 w-full lg:w-1/2">
                  {data.map((d, i) => (
                    <div
                      key={i}
                      className="flex flex-col items-start justify-between p-4 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: d.color }}></div>
                        <h3 className="font-semibold text-gray-800 text-sm">{d.name}</h3>
                      </div>
                      <div className="w-full h-[2px] bg-gray-100 mt-3"></div>
                      <div className="flex justify-between w-full">
                        <span className="font-semibold" style={{ color: d.color }}>
                          {d.value}%
                        </span>
                        <span className="text-sm text-gray-500">{d.members} members</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Right Side - Donut Chart */}
                <div className="relative flex justify-center items-center w-full lg:w-1/2 h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                     <Pie
  data={data}
  dataKey="value"
  nameKey="name"
  cx="50%"
  cy="50%"
  innerRadius={90}
  outerRadius={130}
  paddingAngle={0}
  label={!isMobile ? ({ percent }) => `${(percent * 100).toFixed(0)}%` : false}
  labelLine={false}
>
                        {["#9e4bff", "#ffa500", "#003366", "#ffd700", "#14b8a6", "#2249d2"].map(
                          (color, index) => (
                            <Cell key={`cell-${index}`} fill={color} />
                          )
                        )}
                      </Pie>
                    <Tooltip
  wrapperStyle={{ zIndex: 9999 }}
  position={{ x: "50%", y: 10 }}
/>
                    </PieChart>
                  </ResponsiveContainer>

                  <div className="absolute text-center">
                    <p className="text-2xl font-semibold text-gray-800">EMPLOYEE</p>
                    <p className="text-2xl font-semibold text-gray-800">DISTRIBUTION</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Employee Table */}
          {/* --- CLIENT PROJECT TRACKER SECTION --- */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            
            {successMessage && (
              <div className="mb-4 rounded-md bg-green-100 text-green-700 px-4 py-2 text-sm border border-green-300">
                {successMessage}
              </div>
            )}

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-5 gap-3 flex-wrap">
              {/* Left: Title */}
              <h2 className="text-lg font-semibold text-gray-800">
                Employees Tracker
              </h2>

              {/* Right: Filter + Search + Create */}
              <div className="flex flex-wrap sm:flex-nowrap items-center gap-3">
                <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    disabled={statusLoading || statusError}
                    className="px-3 py-2 text-gray-700 focus:outline-none disabled:bg-gray-100 bg-white"
                  >
                    <option value="All">All</option>
                    {(employeeStatuses || []).map((s) => (
                      <option key={s.id ?? s.emp_status} value={s.emp_status}>
                        {s.emp_status}
                      </option>
                    ))}
                  </select>
                </div>

                {statusError && (
                  <p className="text-sm text-red-600 mt-1">
                    {String(statusError) || "Failed to load statuses"}
                  </p>
                )}

                <div className="relative w-full sm:w-64">
                  <div className="flex items-stretch border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
                    <input
                      type="text"
                      placeholder="Search"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-4 py-2 pr-10 focus:outline-none"
                    />
                  </div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="gray"
                    className="absolute right-3 top-2.5 w-5 h-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 100-15 7.5 7.5 0 000 15z"
                    />
                  </svg>
                </div>

                {(searchTerm || filterStatus !== "All") && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm("");
                      setFilterStatus("All");
                    }}
                    className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition whitespace-nowrap shrink-0 w-full sm:w-auto"
                  >
                    <X size={14} />
                    Clear Filters
                  </button>
                )}

                {canDo("Employees", "Add") && (
                  <button
                    onClick={() => {
                      closeAllModals();
                      setShowModal(true);
                    }}
                   className=" text-white px-4 py-2 rounded-lg shadow flex items-center gap-2 whitespace-nowrap shrink-0"
                   
                   style={{ 
  background: "linear-gradient(135deg, #2563eb, #d12465)"
}}>
                    <Plus className="w-4 h-4" /> Create New Employee
                  </button>
                )}
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left">
                <thead>
                  <tr className="bg-sky-100 text-gray-700 whitespace-nowrap">
                    <th className="py-3 px-4">ID</th>
                    <th className="py-3 px-4">Employee Name</th>
                    <th className="py-3 px-4">Email</th>
                    <th className="py-3 px-4">Phone no.</th>
                    <th className="py-3 px-4">Gender</th>
                    <th className="py-3 px-4">Department</th>
                    <th className="py-3 px-4">Designation</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4">Type</th>
                    <th className="py-3 px-4">Work Location</th>
                    {(canDo("Employees", "Edit") || canDo("Employees", "Delete") || canDo("Employees", "View") ) && (
                      <th className="py-3 text-center w-[15%]">Actions</th>
                    )}
                  </tr>
                </thead>
              <tbody>
                  {paginatedEmployees.map((e, i) => (
                    <tr
                      key={i}
                      className={`border-t transition whitespace-nowrap ${
                        i % 2 === 1 ? "bg-[#f7f6fe]" : "bg-white"
                      } hover:bg-sky-50`}
                    >
                      <td className="py-3 px-4 text-[#8b5cf6]">
                        {e.id}
                      </td>
                      <td className="py-3 px-4">{e.first_name} {e.last_name}</td>
                      <td className="py-3 px-4">{e.email}</td>
                      <td className="py-3 px-4">{e.phone}</td>
                      <td className="py-3 px-4">
                        {e.gender?.charAt(0).toUpperCase() + e.gender?.slice(1)}
                      </td>
                      <td className="py-3 px-4">{e.department}</td>
                      <td className="py-3 px-4">{e.designation}</td>
                      <td className="py-3 px-4">
                        <span
                          className={`px-3 py-1 rounded-full text-sm ${statusColors[e.status]}`}
                        >
                          {e.status}
                        </span>
                      </td>
                      <td className="py-3 px-4">{e.type}</td>
                      <td className="py-3 px-4">{e.workLocation}</td>
                      <td className="py-3 px-4 flex gap-3">
                        {/* {canDo("Employees", "View") && ( */}
                       <Eye
                          className="w-5 h-5 text-blue-500 cursor-pointer"
                          onClick={() => {
                            closeAllModals();
                            setSelectedEmployee(e);
                            setShowViewModal(true);
                          }}
                        />
                        {/* )} */}
                        {canDo("Employees", "Edit") && (
                          <Edit
                            className="w-5 h-5 text-green-500 cursor-pointer"
                            onClick={() => {
                              closeAllModals();
                              setSelectedEmployee(e);
                              setShowEditModal(true);
                            }}
                          />
                        )}

                        {canDo("Employees", "Delete") && (
                        <Trash2
                          className="w-5 h-5 text-red-500 cursor-pointer"
                          onClick={() => {
                            closeAllModals();
                            setSelectedEmployee(e);
                            setShowDeleteModal(true);
                          }}
                        />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

        <div className="flex flex-col sm:flex-row sm:justify-between items-center gap-3 mt-4">
  {/* Records per page */}
  <div className="flex items-center space-x-2">
    <label htmlFor="recordsPerPage" className="text-gray-700 text-sm">Records:</label>
    <div className="border border-gray-300 rounded-md overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
      <select
        id="recordsPerPage"
        className="px-2 py-1 text-sm focus:outline-none bg-white"
        value={itemsPerPage}
        onChange={(e) => {
          setItemsPerPage(Number(e.target.value));
          setPage(1);
        }}
      >
        <option value={5}>5</option>
        <option value={10}>10</option>
        <option value={15}>15</option>
      </select>
    </div>
  </div>

  {/* Pagination controls */}
  <div className="flex items-center space-x-1">
    <button
      onClick={() => setPage(1)}
      disabled={page === 1}
      className="w-8 h-8 sm:w-auto sm:h-auto sm:px-2.5 sm:py-1.5 flex items-center justify-center rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
    >
      <ChevronsLeft size={16} />
    </button>
    <button
      onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
      disabled={page === 1}
      className="w-8 h-8 sm:w-auto sm:h-auto sm:px-2.5 sm:py-1.5 flex items-center justify-center rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
    >
      <MdKeyboardArrowLeft size={16} />
    </button>

    {(() => {
      let start = Math.max(page - 1, 1);
      let end = start + 2;
      if (end > totalPages) { end = totalPages; start = Math.max(end - 2, 1); }
      return Array.from({ length: end - start + 1 }, (_, k) => start + k).map((n) => (
        <button
          key={n}
          onClick={() => setPage(n)}
          className={`w-8 h-8 sm:w-auto sm:h-auto sm:px-3 sm:py-1.5 flex items-center justify-center rounded-lg text-sm font-medium transition-colors
            ${n === page
              ? "text-white bg-[linear-gradient(135deg,#2563eb,#d12465)]"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
        >
          {n}
        </button>
      ));
    })()}

    <button
      onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
      disabled={page === totalPages}
      className="w-8 h-8 sm:w-auto sm:h-auto sm:px-2.5 sm:py-1.5 flex items-center justify-center rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
    >
      <MdKeyboardArrowRight size={16} />
    </button>
    <button
      onClick={() => setPage(totalPages)}
      disabled={page === totalPages}
      className="w-8 h-8 sm:w-auto sm:h-auto sm:px-2.5 sm:py-1.5 flex items-center justify-center rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
    >
      <ChevronsRight size={16} />
    </button>
  </div>
</div>
          </div>

        </div>

       {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center px-4 z-50">
            <div
  ref={modalRef} 
  className="bg-white rounded-2xl w-full max-w-2xl p-4 sm:p-6 shadow-lg relative overflow-y-auto max-h-[90vh]"
>
              <button
                onClick={closeCreateModal}
                className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 z-10"
              >
                <X size={22} />
              </button>

              <h2 className="text-xl font-semibold mb-4 text-gray-800 pr-8">
                Add New Employee
              </h2>
{errorMessage && (
              <div className="mb-4 rounded-md bg-red-100 text-red-700 px-4 py-2 text-sm border border-red-300">
                {errorMessage}
              </div>
            )}

            <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" >


  {/* Picture Upload */}
  <div className="col-span-1 sm:col-span-2">
    <label className="block text-sm font-medium mb-2 text-gray-700">
      Picture Upload
    </label>
    <div
      className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center text-gray-500 cursor-pointer hover:bg-gray-50 transition"
      onClick={() => document.getElementById("fileInput").click()}
    >
      {preview ? (
        <div className="flex flex-col items-center gap-2">
          <img
            src={preview}
            alt="Employee Preview"
            className="mx-auto w-24 h-24 object-cover rounded-full border"
          />
          <button
            type="button"
            onClick={(ev) => {
              ev.stopPropagation();
              setPreview(null);
              const input = document.getElementById("fileInput");
              if (input) input.value = "";
            }}
            className="text-xs text-red-500 underline"
          >
            Remove photo
          </button>
        </div>
      ) : (
        <>
          <UploadCloud className="mx-auto mb-2" />
          <p className="font-medium text-gray-700">
            Drag your file or <span className="text-[#8c57ff] underline">browse</span>
          </p>
          <p className="text-xs text-gray-400">
            Only support: jpg, jpeg, png | Max: 1MB
          </p>
        </>
      )}
    </div>
    <input
      id="fileInput"
      type="file"
      name="image"
      accept=".jpg,.jpeg,.png"
      className="hidden"
      onChange={handleImageChange}
    />
  </div>

  {/* Role select */}
  <div className="col-span-1 sm:col-span-2">
    <label className="block text-sm font-medium mb-1 text-gray-700">
      Role
    </label>
    <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
      <select
        name="role_id"
        required
        disabled={roleLoading || roleError}
        className="px-3 py-2 w-full focus:outline-none disabled:bg-gray-100 bg-white"
        defaultValue=""
      >
        <option value="" disabled>
          {roleLoading ? "Loading role..." : "Select a Role"}
        </option>
        {(roles || [])
    .filter((r) => r.status === 'active') // only active roles
    .map((r) => (
      <option key={r.id} value={r.id} data-name={r.role_name}>
        {r.role_name}
      </option>
    ))
  }
      </select>
    </div>
    {roleError && (
      <p className="mt-1 text-sm text-red-600">
        {String(roleError) || "Failed to load departments"}
      </p>
    )}
  </div>


        {/* First Name */}
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-700">
            First Name
          </label>
          <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
            <input
              name="firstName"
              type="text"
              placeholder="Enter first name"
              required
              className="px-3 py-2 w-full focus:outline-none"
            />
          </div>
        </div>

        {/* Last Name */}
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-700">
            Last Name
          </label>
          <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
            <input
              name="lastName"
              type="text"
              placeholder="Enter last name"
              required
              className="px-3 py-2 w-full focus:outline-none"
            />
          </div>
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-700">
            Email
          </label>
          <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
            <input
              name="email"
              type="email"
              placeholder="Enter email address"
              required
              className="px-3 py-2 w-full focus:outline-none"
            />
          </div>
        </div>

        {/* Phone Number */}
       <div>
        <label className="block text-sm font-medium mb-1 text-gray-700">
          Phone Number
        </label>
        <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
          <input
            name="phone"
            type="tel"
            placeholder="Enter phone number"
            required
            inputMode="numeric"
            maxLength={10}
            pattern="[6-9][0-9]{9}"
            title="Enter a valid 10-digit Indian phone number"
            onInput={(e) => {
              e.target.value = e.target.value.replace(/\D/g, "");
            }}
            className="px-3 py-2 w-full focus:outline-none"
          />
        </div>
      </div>

        {/* Department */}
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-700">
            Department
          </label>
          <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
            <select
              name="department"                  // stays 'department' because backend expects name
              required
              disabled={deptLoading || deptError}
              className="px-3 py-2 w-full focus:outline-none disabled:bg-gray-100 bg-white"
              defaultValue=""
            >
              <option value="" disabled>
                {deptLoading ? "Loading departments..." : "Select a Department"}
              </option>

              {(departments || []).map((d) => (
                <option key={d.id} value={d.id} data-name={d.name}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
          {deptError && (
            <p className="mt-1 text-sm text-red-600">
              {String(deptError) || "Failed to load departments"}
            </p>
          )}
        </div>

        {/* Designation */}
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-700">
            Designation
          </label>
          <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
            <select
              name="designation"
              required
              disabled={desigLoading || desigError}
              className="px-3 py-2 w-full focus:outline-none disabled:bg-gray-100 bg-white"
              defaultValue=""
            >
              <option value="" disabled>
                {desigLoading ? "Loading designations..." : "Select a designation"}
              </option>

              {(designations || []).map((d) => (
                <option key={d.id} value={d.id} data-name={d.name}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>

          {desigError && (
            <p className="mt-1 text-sm text-red-600">
              {String(desigError) || "Failed to load designations"}
            </p>
          )}
        </div>

        {/* Reporting Manager */}
       <div>
          <label className="block text-sm font-medium mb-1 text-gray-700">
            Reporting Manager
          </label>
          <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
            <select
              name="reporting_manager"
              className="px-3 py-2 w-full focus:outline-none bg-white"
              defaultValue=""
            >
              <option value="">Select Reporting Manager (Optional)</option>
              {employees.map((emp) => (
                <option key={emp.emp_id} value={emp.empId}>
                  {emp.first_name} {emp.last_name} {/* Display name only */}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Employee Type */}
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-700">
            Employee Type
          </label>
          <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
            <select
              name="employee_type"
              required
              disabled={typeLoading || typeError}
              className="px-3 py-2 w-full focus:outline-none disabled:bg-gray-100 bg-white"
              defaultValue=""
            >
              <option value="" disabled>
                {typeLoading ? "Loading types..." : "Select Employee Type"}
              </option>

              {(employeeTypes || []).map((t) => (
                <option key={t.emp_type_id} value={t.emp_type_id} data-name={t.emp_type}>
                  {t.emp_type}
                </option>
              ))}
            </select>
          </div>

          {typeError && (
            <p className="mt-1 text-sm text-red-600">
              {String(typeError) || "Failed to load employee types"}
            </p>
          )}
        </div>

        {/* Work Location */}
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-700">
            Work Location
          </label>
          <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
            <select
              name="work_location"
              required
              disabled={workLocationLoading || workLocationError}
              className="px-3 py-2 w-full focus:outline-none disabled:bg-gray-100 bg-white"
              defaultValue=""
            >
              <option value="" disabled>
                {workLocationLoading ? "Loading locations..." : "Select Work Location"}
              </option>

              {(workLocations || []).map((loc) => (
                <option key={loc.wl_id} value={loc.wl_id} data-name={loc.work_location}>
                  {loc.work_location}
                </option>
              ))}
            </select>
          </div>

          {workLocationError && (
            <p className="mt-1 text-sm text-red-600">
              {String(workLocationError) || "Failed to load work locations"}
            </p>
          )}
        </div>


        {/* Gender */}
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-700">
            Gender
          </label>
          <div className="flex flex-wrap gap-4 sm:gap-6 items-center mt-1">
            <label className="flex items-center gap-2">
              <input type="radio" name="gender" value="Male" defaultChecked />
              <span>Male</span>
            </label>
            <label className="flex items-center gap-2">
              <input type="radio" name="gender" value="Female" />
              <span>Female</span>
            </label>
          </div>
        </div>

        {/* Status */}
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-700">
            Status
          </label>
          <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
            <select
              name="status"
              required
              disabled={statusLoading || statusError}
              className="px-3 py-2 w-full focus:outline-none disabled:bg-gray-100 bg-white"
              defaultValue=""
            >
              <option value="" disabled>
                {statusLoading ? "Loading statuses..." : "Select Status"}
              </option>

              {(employeeStatuses || []).map((s) => (
                <option key={s.id} value={s.id} data-name={s.emp_status}>
                  {s.emp_status}
                </option>
              ))}
            </select>
          </div>

          {statusError && (
            <p className="mt-1 text-sm text-red-600">
              {String(statusError) || "Failed to load employee statuses"}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1 text-gray-700">
            Date Of Birth
          </label>
          <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
            <input
              name="dob"
              type="date"
              max={getMaxDob()}
              required
              className="px-3 py-2 w-full focus:outline-none"
            />
          </div>
        </div>

        <div className="col-span-1 sm:col-span-2">
          <label className="block text-sm font-medium mb-1 text-gray-700">
            Address
          </label>
          <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
            <textarea
              name="address"
              placeholder="Enter Address"
              required
              className="px-3 py-2 w-full focus:outline-none"
              rows={3} // You can adjust the number of visible rows
            />
          </div>
        </div>

        <div>
  <label className="block text-sm font-medium mb-1 text-gray-700">
    Postcode
  </label>
  <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
    <input
      name="postcode"
      type="text"
      placeholder="Postcode"
      required
      inputMode="numeric"
      maxLength={6}
      pattern="^[1-9][0-9]{5}$"
      title="Enter a valid 6-digit Indian PIN code"
      onInput={(e) => {
        e.target.value = e.target.value.replace(/\D/g, "");
      }}
      className="px-3 py-2 w-full focus:outline-none"
    />
  </div>
</div>

        <div>
          <label className="block text-sm font-medium mb-1 text-gray-700">
            Joining Date
          </label>
          <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
            <input
              name="joiningdate"
              type="date"
              required
              className="px-3 py-2 w-full focus:outline-none"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1 text-gray-700">
            Bank Name
          </label>
          <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
            <input
              name="bank"
              type="text"
              placeholder="Bank Name"
              required
              className="px-3 py-2 w-full focus:outline-none"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1 text-gray-700">
            IFSC Code
          </label>
          <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
            <input
              name="ifsc"
              type="text"
              placeholder="IFSC Code"
              required
              className="px-3 py-2 w-full focus:outline-none"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1 text-gray-700">
            Account Number
          </label>
          <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
            <input
              name="acc_no"
              type="text"
              placeholder="Account Number"
              required
              className="px-3 py-2 w-full focus:outline-none"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1 text-gray-700">
            Pan Number
          </label>
          <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
            <input
              name="pan_no"
              type="text"
              placeholder="Pan Number"
              required
              maxLength={10}
              pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
              title="Enter a valid PAN (e.g. ABCDE1234F)"
              onInput={(e) => {
                e.target.value = e.target.value.toUpperCase();
              }}
              className="px-3 py-2 w-full focus:outline-none"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1 text-gray-700">
            UAN Number
          </label>
          <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
            <input
              name="uan_no"
              type="text"
              placeholder="UAN Number"
              className="px-3 py-2 w-full focus:outline-none"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1 text-gray-700">
            Salary (per month)
          </label>

          <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
            <input
              name="salary"
              type="text"
              placeholder="Enter salary"
              required
              inputMode="numeric"
              onInput={(e) => {
                e.target.value = e.target.value.replace(/\D/g, "");
              }}
              className="px-3 py-2 w-full focus:outline-none"
            />
          </div>
        </div>


        {/* Buttons */}
        <div className="col-span-1 sm:col-span-2 flex justify-end gap-3 mt-3">
          <button
            type="button"
            onClick={closeCreateModal}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-white rounded-lg"
        style={{ 
  background: "linear-gradient(135deg, #2563eb, #d12465)"
}}  >
            Create New Employee
          </button>
        </div>
      </form>
    </div>
  </div>
)}

 {/* --- VIEW MODAL --- */}
      {showViewModal && selectedEmployee && (
       <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center px-4 z-50">
  <div className="bg-white rounded-2xl p-4 sm:p-6 w-full max-w-3xl relative max-h-[85vh] overflow-y-auto">
    <button
      onClick={() => setShowViewModal(false)}
      className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 z-10"
    >
      <X size={22} />
    </button>
            <h2 className="text-xl font-semibold mb-4 text-gray-900 pr-8">Employee Details</h2>

            {/* Top: Avatar + Name + Status */}
            <div className="flex items-center gap-4 mb-5 flex-wrap">
              <div className="h-12 w-12 rounded-full text-white flex items-center justify-center text-lg font-semibold"
              style={{
background: "linear-gradient(135deg, #2563eb, #d12465)"
}}>
                {(selectedEmployee?.first_name || "N/A").charAt(0).toUpperCase()}
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-semibold text-gray-900">
                  {selectedEmployee?.first_name || "N/A"} {selectedEmployee?.last_name || "N/A"}
                </span>
                {/* <span className="text-sm text-gray-500">
                  {selectedEmployee?.designation || "—"} · {selectedEmployee?.department || "—"}
                </span> */}
              </div>
              <span
                className={[
                  "sm:ml-auto inline-flex items-center rounded-full px-3 py-1 text-xs font-medium",
                  (selectedEmployee?.status || "").toLowerCase() === "active"
                    ? "bg-green-100 text-green-700"
                    : (selectedEmployee?.status || "").toLowerCase() === "onboarding"
                    ? "bg-yellow-100 text-yellow-700"
                    : (selectedEmployee?.status || "").toLowerCase() === "probation"
                    ? "bg-orange-100 text-orange-700"
                    : (selectedEmployee?.status || "").toLowerCase() === "inactive"
                    ? "bg-red-200 text-red-700"
                    : "bg-slate-100 text-slate-700",
                ].join(" ")}
              >
                {selectedEmployee?.status || "—"}
              </span>
            </div>

            {/* Info Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* IDs */}
              <div className="rounded-lg border border-gray-100 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500">Employee ID</p>
                <p className="mt-0.5 font-medium text-gray-900">
                  {selectedEmployee?.id || selectedEmployee?.emp_id || "—"}
                </p>
              </div>
              <div className="rounded-lg border border-gray-100 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500">Role</p>
                <p className="mt-0.5 font-medium text-gray-900">
                  {selectedEmployee?.role_name || selectedEmployee?.role_name || "—"}
                </p>
              </div>

              <div className="rounded-lg border border-gray-100 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500">Reporting To</p>
                <p className="mt-0.5 font-medium text-gray-900">
                  {selectedEmployee?.reporting_manager_name || selectedEmployee?.reporting_manager_name || "—"}
                </p>
              </div>

              <div className="rounded-lg border border-gray-100 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500">Type</p>
                <p className="mt-0.5 font-medium text-gray-900">
                  {selectedEmployee?.employee_type || selectedEmployee?.type || "—"}
                </p>
              </div>

              {/* Contact */}
              <div className="rounded-lg border border-gray-100 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500">Email</p>
                <p className="mt-0.5 font-medium text-gray-900 break-all">
                  {selectedEmployee?.email || "—"}
                </p>
              </div>
              <div className="rounded-lg border border-gray-100 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500">Phone</p>
                <p className="mt-0.5 font-medium text-gray-900">
                  {selectedEmployee?.phone || "—"}
                </p>
              </div>

              {/* Org */}
              <div className="rounded-lg border border-gray-100 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500">Department</p>
                <p className="mt-0.5 font-medium text-gray-900">
                  {selectedEmployee?.department || "—"}
                </p>
              </div>
              <div className="rounded-lg border border-gray-100 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500">Designation</p>
                <p className="mt-0.5 font-medium text-gray-900">
                  {selectedEmployee?.designation || "—"}
                </p>
              </div>

              {/* Personal */}
              <div className="rounded-lg border border-gray-100 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500">Type</p>
                <p className="mt-0.5 font-medium text-gray-900">
                  {selectedEmployee?.type || "—"}
                </p>
              </div>
              <div className="rounded-lg border border-gray-100 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500">Gender</p>
                <p className="mt-0.5 font-medium text-gray-900 capitalize">
                  {selectedEmployee?.gender || "—"}
                </p>
              </div>
              <div className="rounded-lg border border-gray-100 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500">Work Location</p>
                <p className="mt-0.5 font-medium text-gray-900">
                  {selectedEmployee?.workLocation || selectedEmployee?.location || "—"}
                </p>
              </div>

              {/* Dates (optional if present) */}
              <div className="rounded-lg border border-gray-100 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500">Date Of Birth</p>
                <p className="mt-0.5 font-medium text-gray-900">
                  {selectedEmployee?.dob || "—"}
                </p>
              </div>

              <div className="rounded-lg border border-gray-100 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500">Joining Date</p>
                <p className="mt-0.5 font-medium text-gray-900">
                  {selectedEmployee?.joiningDate
                    ? new Date(selectedEmployee.joiningDate).toLocaleDateString()
                    : "—"}
                </p>
              </div>

              <div className="rounded-lg border border-gray-100 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500">Exit dte</p>
                <p className="mt-0.5 font-medium text-gray-900">
                  {selectedEmployee?.exit_date || selectedEmployee?.exit_date || "—"}
                </p>
              </div>

              <div className="rounded-lg border border-gray-100 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500">Address</p>
                <p className="mt-0.5 font-medium text-gray-900">
                  {selectedEmployee?.address || selectedEmployee?.address || "—"}
                </p>
              </div>

              <div className="rounded-lg border border-gray-100 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500">Postcode</p>
                <p className="mt-0.5 font-medium text-gray-900">
                  {selectedEmployee?.postcode || selectedEmployee?.postcode || "—"}
                </p>
              </div>

              <div className="rounded-lg border border-gray-100 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500">Bank Name</p>
                <p className="mt-0.5 font-medium text-gray-900">
                  {selectedEmployee?.bank || selectedEmployee?.bank || "—"}
                </p>
              </div>

              <div className="rounded-lg border border-gray-100 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500">IFSC Code</p>
                <p className="mt-0.5 font-medium text-gray-900">
                  {selectedEmployee?.ifsc || selectedEmployee?.ifsc || "—"}
                </p>
              </div>

              <div className="rounded-lg border border-gray-100 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500">Account Number</p>
                <p className="mt-0.5 font-medium text-gray-900">
                  {selectedEmployee?.acc_no || selectedEmployee?.acc_no || "—"}
                </p>
              </div>

              <div className="rounded-lg border border-gray-100 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500">UAN Number</p>
                <p className="mt-0.5 font-medium text-gray-900">
                  {selectedEmployee?.uan_no || selectedEmployee?.uan_no || "—"}
                </p>
              </div>

              <div className="rounded-lg border border-gray-100 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500">Salary</p>
                <p className="mt-0.5 font-medium text-gray-900">
                  {selectedEmployee?.salary || selectedEmployee?.salary || "—"}
                </p>
              </div>
            </div>

            {/* Notes / About (optional) */}
            {selectedEmployee?.notes && (
              <div className="mt-4 rounded-lg border border-gray-100 p-3">
                <p className="text-xs uppercase tracking-wide text-gray-500">Notes</p>
                <p className="mt-0.5 text-gray-800 whitespace-pre-wrap">
                  {selectedEmployee.notes}
                </p>
              </div>
            )}

          </div>
        </div>
      )}

      {/* --- EDIT MODAL --- */}
      {showEditModal && selectedEmployee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center px-4 z-50">
          <div className="bg-white rounded-2xl w-full max-w-2xl p-4 sm:p-6 shadow-lg relative overflow-y-auto max-h-[90vh]">

            <button
              onClick={() => { setShowEditModal(false); setPreview(null); }}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 z-10"
            >
              <X size={22} />
            </button>
            <h2 className="text-xl font-semibold mb-4 text-gray-800 pr-8">
              Edit Employee
            </h2>

            <form
              onSubmit={handleEditSubmit}
              className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-white p-4 rounded-xl"
            >

              {/* Picture Upload */}
              <div className="col-span-1 sm:col-span-2">
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  Picture Upload
                </label>

                <div
                  className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center text-gray-500 cursor-pointer hover:bg-gray-50 transition"
                  onClick={() => document.getElementById("fileInput").click()}
                >
                  {/* Show preview if image exists */}
                  {preview || selectedEmployee.image ? (
                    <img
                      src={preview || selectedEmployee.image}
                      alt="Employee Preview"
                      className="mx-auto mb-3 w-24 h-24 object-cover rounded-full border"
                    />
                  ) : (
                    <>
                      <UploadCloud className="mx-auto mb-2" />
                      <p className="font-medium text-gray-700">
                        Drag your file or{" "}
                        <span className="text-[#8c57ff] underline">browse</span>
                      </p>
                      <p className="text-xs text-gray-400">
                        Only support: jpg, jpeg, png | Max: 1MB
                      </p>
                    </>
                  )}
                </div>

                  <input
                    id="fileInput"
                    type="file"
                    name="image"
                    accept=".jpg,.jpeg,.png"
                    className="hidden"
                    onChange={(e) => handleImageChange(e)}
                  />
                </div>


              {/* Role Dropdown */}
              <div className="col-span-1 sm:col-span-2">
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  Role
                </label>
                <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
                  <select
                    name="role_id"
                    required
                    disabled={roleLoading || roleError}
                    defaultValue={selectedEmployee.role_id || ""}
                    className="px-3 py-2 w-full focus:outline-none disabled:bg-gray-100 bg-white"
                  >
                    <option value="" disabled>
                      {roleLoading ? "Loading roles..." : "Select Role"}
                    </option>
                    {(roles || []).filter((r) => r.status === 'active').map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.role_name}
                      </option>
                    ))}
                  </select>
                </div>
                {roleError && (
                  <p className="text-sm text-red-600 mt-1">Failed to load Roles</p>
                )}
              </div>

              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  First Name
                </label>
                <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
                  <input
                    name="first_name"
                    type="text"
                    defaultValue={selectedEmployee.first_name}
                    placeholder="Enter First name"
                    required
                    className="px-3 py-2 w-full focus:outline-none"
                  />
                </div>
              </div>

              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  Last Name
                </label>
                <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
                  <input
                    name="last_name"
                    type="text"
                    defaultValue={selectedEmployee.last_name}
                    placeholder="Enter Last name"
                    required
                    className="px-3 py-2 w-full focus:outline-none"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  Email Address
                </label>
                <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
                  <input
                    name="email"
                    type="email"
                    defaultValue={selectedEmployee.email}
                    placeholder="Enter email address"
                    required
                    className="px-3 py-2 w-full focus:outline-none"
                  />
                </div>
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  Phone Number
                </label>
                <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
                  <input
                    name="phone"
                    type="tel"
                    defaultValue={selectedEmployee.phone}
                    placeholder="Enter phone number"
                    pattern="^\+?[0-9]{0,3}?[-.\s]?\(?[0-9]{1,4}?\)?[-.\s]?[0-9]{3,4}[-.\s]?[0-9]{3,4}$"
                    required
                    className="px-3 py-2 w-full focus:outline-none"
                  />
                </div>
              </div>

              {/* Department Dropdown */}
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  Department
                </label>
                <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
                  <select
                    name="department"
                    required
                    disabled={deptLoading || deptError}
                    defaultValue={selectedEmployee.department_id || ""}
                    className="px-3 py-2 w-full focus:outline-none disabled:bg-gray-100 bg-white"
                  >
                    <option value="" disabled>
                      {deptLoading ? "Loading departments..." : "Select Department"}
                    </option>
                    {(departments || []).map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>
                {deptError && (
                  <p className="text-sm text-red-600 mt-1">Failed to load departments</p>
                )}
              </div>

              {/* Designation Dropdown */}
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  Designation
                </label>
                <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
                  <select
                    name="designation"
                    required
                    disabled={desigLoading || desigError}
                    defaultValue={selectedEmployee.designation_id || ""}
                    className="px-3 py-2 w-full focus:outline-none disabled:bg-gray-100 bg-white"
                  >
                    <option value="" disabled>
                      {desigLoading ? "Loading designations..." : "Select Designation"}
                    </option>
                    {(designations || []).map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>
                {desigError && (
                  <p className="text-sm text-red-600 mt-1">Failed to load designations</p>
                )}
              </div>

              {/* Reporting Manager */}
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  Reporting Manager
                </label>
                <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
                  <select
                    name="reporting_manager"
                    className="px-3 py-2 w-full focus:outline-none bg-white"
                    defaultValue={
                      selectedEmployee.reporting_manager ||
                      employees.find(
                        (emp) =>
                          `${emp.first_name} ${emp.last_name}` ===
                          selectedEmployee.reporting_manager_name
                      )?.empId ||
                      ""
                    }
                  >
                    <option value="">Select Reporting Manager (Optional)</option>
                    {employees
                      .filter((emp) => emp.empId !== selectedEmployee.empId) // an employee can't report to themselves
                      .map((emp) => (
                        <option key={emp.emp_id} value={emp.empId}>
                          {emp.first_name} {emp.last_name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              {/* Employment Status */}
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  Employment Status
                </label>
                <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
                  <select
                    name="status"
                    required
                    disabled={statusLoading || statusError}
                    defaultValue={selectedEmployee.status_id || ""}
                    className="px-3 py-2 w-full focus:outline-none disabled:bg-gray-100 bg-white"
                  >
                    <option value="" disabled>
                      {statusLoading ? "Loading statuses..." : "Select Status"}
                    </option>
                    {(employeeStatuses || []).map((s) => (
                      <option key={s.id } value={s.id}>
                        {s.emp_status}
                      </option>
                    ))}
                  </select>
                </div>
                {statusError && (
                  <p className="text-sm text-red-600 mt-1">
                    {String(statusError) || "Failed to load statuses"}
                  </p>
                )}
              </div>

              {/* Employee Type */}
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  Employee Type
                </label>
                <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
                  <select
                    name="employee_type"
                    required
                    disabled={typeLoading || typeError}
                    defaultValue={employeeTypes?.find(t => t.emp_type === selectedEmployee?.type)?.emp_type_id || ""}
                    className="px-3 py-2 w-full focus:outline-none disabled:bg-gray-100 bg-white"
                  >
                    <option value="" disabled>
                      {typeLoading ? "Loading types..." : "Select Employee Type"}
                    </option>
                    {(employeeTypes || []).map((t) => (
                      <option
                        key={t.emp_type_id}
                        value={t.emp_type_id}
                      >
                        {t.emp_type}
                      </option>
                    ))}
                  </select>
                </div>
                {typeError && (
                  <p className="text-sm text-red-600 mt-1">
                    {String(typeError) || "Failed to load employee types"}
                  </p>
                )}
              </div>

              {/* Gender */}
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  Gender
                </label>

                <div className="flex flex-wrap gap-4 sm:gap-6 items-center mt-1">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="gender"
                      value="Male"
                      defaultChecked={selectedEmployee.gender === "male"}
                    />
                    <span>Male</span>
                  </label>

                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="gender"
                      value="Female"
                      defaultChecked={selectedEmployee.gender === "female"}
                    />
                    <span>Female</span>
                  </label>
                </div>
              </div>


              {/* Work Location */}
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  Work Location
                </label>
                <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
                  <select
                    name="work_location"
                    required
                    disabled={workLocationLoading || workLocationError}
                    defaultValue={workLocations?.find(loc => loc.work_location === selectedEmployee?.workLocation)?.wl_id || ""}
                    className="px-3 py-2 w-full focus:outline-none disabled:bg-gray-100 bg-white"
                  >
                    <option value="" disabled>
                      {workLocationLoading ? "Loading locations..." : "Select Work Location"}
                    </option>
                    {(workLocations || []).map((loc) => (
                      <option
                        key={loc.wl_id}
                        value={loc.wl_id}
                      >
                        {loc.work_location}
                      </option>
                    ))}
                  </select>
                </div>
                {workLocationError && (
                  <p className="text-sm text-red-600 mt-1">
                    {String(workLocationError) || "Failed to load work locations"}
                  </p>
                )}
              </div>

              {/* Date of Birth */}
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  Date Of Birth
                </label>
                <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
                  <input
                    name="dob"
                    type="date"
                    max={getMaxDob()}
                    defaultValue={
                      selectedEmployee.dob
                        ? selectedEmployee.dob.split("T")[0] // format if it includes time
                        : ""
                    }
                    placeholder="Select Joining Date"
                    required
                    className="px-3 py-2 w-full focus:outline-none"
                  />
                </div>
              </div>

              {/* Exit Date  */}
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  Exit Date
                </label>
                <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
                  <input
                    name="exit_date"
                    type="date"
                    defaultValue={selectedEmployee.exit_date}
                    placeholder="Enter Postcode"
                    className="px-3 py-2 w-full focus:outline-none"
                  />
                </div>
              </div>

              <div className="col-span-1 sm:col-span-2">
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  Address
                </label>
                <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
                  <textarea
                    name="address"
                    placeholder="Enter Address"
                    required
                    defaultValue={selectedEmployee.address}
                    className="px-3 py-2 w-full focus:outline-none"
                    rows={3} // You can adjust the number of visible rows
                  />
                </div>
              </div>

              {/* Postcode  */}
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  Postcode
                </label>
                <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
                  <input
                    name="postcode"
                    type="text"
                    defaultValue={selectedEmployee.postcode}
                    placeholder="Enter Postcode"
                    required
                    inputMode="numeric"
                    maxLength={6}
                    pattern="^[1-9][0-9]{5}$"
                    title="Enter a valid 6-digit Indian PIN code"
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, "");
                    }}
                    className="px-3 py-2 w-full focus:outline-none"
                  />
                </div>
              </div>

              {/* Joining Date */}
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  Joining Date
                </label>
                <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
                  <input
                    name="joiningDate"
                    type="date"
                    defaultValue={
                      selectedEmployee.joiningDate
                        ? selectedEmployee.joiningDate.split("T")[0] // format if it includes time
                        : ""
                    }
                    placeholder="Select Joining Date"
                    required
                    className="px-3 py-2 w-full focus:outline-none"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  Bank Name
                </label>
                <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
                  <input
                    name="bank"
                    type="text"
                    defaultValue={selectedEmployee.bank}
                    placeholder="Bank Name"
                    required
                    className="px-3 py-2 w-full focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  IFSC Code
                </label>
                <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
                  <input
                    name="ifsc"
                    type="text"
                    placeholder="IFSC Code"
                    defaultValue={selectedEmployee.ifsc}
                    required
                    className="px-3 py-2 w-full focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  Account Number
                </label>
                <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
                  <input
                    name="acc_no"
                    type="text"
                    defaultValue={selectedEmployee.acc_no}
                    placeholder="Account Number"
                    required
                    className="px-3 py-2 w-full focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  Pan Number
                </label>
                <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
                  <input
                    name="pan_no"
                    type="text"
                    defaultValue={selectedEmployee.pan_no ?? selectedEmployee.pan_number ?? ""}
                    placeholder="Pan Number"
                    required
                    maxLength={10}
                    pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
                    title="Enter a valid PAN (e.g. ABCDE1234F)"
                    onInput={(e) => {
                      e.target.value = e.target.value.toUpperCase();
                    }}
                    className="px-3 py-2 w-full focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  UAN Number
                </label>
                <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
                  <input
                    name="uan_no"
                    type="text"
                    placeholder="UAN Number"
                    defaultValue={selectedEmployee.uan_no}
                    required
                    className="px-3 py-2 w-full focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  Salary (per month)
                </label>

                <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
                  <input
                    name="salary"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="Enter monthly salary"
                    defaultValue={selectedEmployee.salary ?? ""}
                    required
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, "");
                    }}
                    className="px-3 py-2 w-full focus:outline-none"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="col-span-1 sm:col-span-2 flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowEditModal(false); setPreview(null); }}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2  text-white rounded-lg shadow hover:opacity-90 transition"
                  style={{
                    background: "linear-gradient(135deg, #2563eb, #d12465)"
                  }} >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- DELETE CONFIRMATION MODAL --- */}
      {showDeleteModal && selectedEmployee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center px-4 z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm text-center relative">
            <button
              onClick={() => setShowDeleteModal(false)}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
            >
              <X size={22} />
            </button>
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Confirm Delete
            </h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete{" "}
              <span className="font-semibold text-red-600">
                {selectedEmployee.first_name} {selectedEmployee.last_name} - {selectedEmployee.id}
              </span>
              ?
            </p>

            <div className="flex justify-center gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 border rounded-lg text-gray-600"
              >
                Cancel
              </button>
              <button onClick={() => { handleDeleteEmployee(); }}
  className="px-4 py-2 bg-red-500 text-white rounded-lg"
>
  Delete
</button>

            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
}